import React, { useState, useEffect, useMemo } from 'react'
import Maybe from 'components/__new/common/Maybe';
import { ReactComponent as IconSearch } from "assets/images/icons/icon-search.svg";
import { ReactComponent as IconAdd } from "assets/images/icons/project-icons/plus-light.svg"
import { ReactComponent as IconCheckCircle } from "assets/images/icons/project-icons/check-circle-green-bg.svg"
import { ReactComponent as IconRemove } from "assets/images/icons/project-icons/times-light.svg"
import { useDispatch, useSelector } from 'react-redux';
import * as walletAction from "store/entities/wallet/action";
import * as banksActions from "store/ui/banks/action";
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useLoginInfo from 'hooks/useLoginInfo';
import CustomCheckbox from 'components/__new/common/CustomCheckbox';
import { getTransferBeneficiaries } from 'store/entities/transfers/action';
import useEmptyData from 'hooks/useEmptyData';
import { SelectInput } from 'components/__new/common/form';
import ReactSelect from 'react-select';
import { reactSelectStyles } from 'utils/reactSelectStyles';
import useBanks from 'hooks/useBanks';


const BankMultipeTransfer = ({
    toggleBankTransferType,
    toggleTransferDestination,
    isWideViewOpen,
    setIsWideViewOpen,
    bankPreviewItems,
    setBankPreviewItems,
    selectedSourceAccount, 
    setSelectedSourceAccount
}) => {
    
    const {userInfo} = useLoginInfo();
    const dispatch = useDispatch();
    const { banks, banksLoading } = useBanks(userInfo?.token);
    const { accountValidationLoading, accountValidation, accountValidationError } = useSelector((s) => s.entities.wallet);
    const { getTransferBeneficiariesLoading, getTransferBeneficiariesData } = useSelector((state) => state.entities.transfers)
    const { businessAccounts, businessAccountsLoading, sourceWalletId } = useSelector(s => s.entities.wallet);
    const expenseAccounts = businessAccounts?.status === "success" && businessAccounts?.data?.filter(account => account?.walletType.toLowerCase() === 'expense') || [];

    const [form, setForm] = useState({
        accountNumber: "",
        accountName: "",
        bankCode: "",
        bankName: "",
        walletId: sourceWalletId ?? "",
        amount: "",
        description: "",
    });
    const [beneficiariesShown, setBeneficiariesShown] = useState(false);

    const [validatedBeneficiary, setValidatedBeneficiary] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const bankAccountInputValid = form?.bankCode?.length > 0 && form?.accountNumber?.length === 10

    const banksData = useMemo(() => {
        return banks?.sort((a, b) => (a?.bankName || a?.institutionName)?.localeCompare(b?.bankName || b?.institutionName))?.map((singleBank) => ({
            label: singleBank?.bankName || singleBank?.institutionName,
            value: singleBank?.bankCode || singleBank?.institutionCode,
        })) || [];
    }, [banks]);
    
    const addBatchBeneficiaries = (batch) => {
        const duplicateAccountNumber = bankPreviewItems.some(obj1 => {
            return batch.some(obj2 => obj1.accountNumber === obj2.accountNumber);
        });

        if(duplicateAccountNumber === true) {
            toast.error("Duplicate beneficiaries can't be added")
        } else {
            setBankPreviewItems([...bankPreviewItems, ...batch])
            setBeneficiariesShown(false);
            setIsWideViewOpen(true);
        }
    }
    
    const onClose = () => {
        toggleBankTransferType();
        toggleTransferDestination();
    }
    
    const resetValues = () => {
        setForm({accountNumber: "", bankCode: "", walletId: sourceWalletId ?? form?.walletId});
        setValidatedBeneficiary(null);
    }
    
    const onSubmit = (e) => e.preventDefault();
    
    const addAnotherBeneficiary = () => {
        if(bankPreviewItems?.length === 20) {
            toast.error(`You can only add ${bankPreviewItems?.length} recipients`)
        } else {
            if(validatedBeneficiary === null) {
                toast.error("Please select an existing beneficiary or enter new account details")
            }
            /**RETURN THIS PART AFTER TESTING */
            // else {
            //     const duplicateItem = bankPreviewItems?.find((item) => item?.accountNumber === validatedBeneficiary?.accountNumber)
            //     if(duplicateItem !== undefined) {
            //         toast.error(`${duplicateItem?.accountName.toUpperCase()} has already been added`)
            //     }
                else {
                    setBankPreviewItems([
                        ...bankPreviewItems,
                        {...form, amount: 0, fee: 0}
                    ])
                    setIsWideViewOpen(true);
                    resetValues();
                }

                // dispatch source wallet to state
                dispatch(walletAction.setSourceWalletId(form?.walletId))
        }
    }
    
    const updateBankCode = (code) => {
        const actualBank = banks?.find((bank) => bank?.institutionCode === code || bank?.bankCode === code);
        setForm({
            ...form,
            accountNumber: '',
            bankCode: code,
            bankName: actualBank?.institutionName || actualBank?.bankName
        });
        setValidatedBeneficiary(null);
    }
    
    useEffect(() => {
        if(bankAccountInputValid) {
            // const params = { bankCode: form.bankCode, accountNumber: form.accountNumber };
            const params = { bankCode: form?.bankCode, accountNumber: form?.accountNumber };
            dispatch(walletAction.validateAccount(userInfo.token, params));
        }
        // eslint-disable-next-line
    }, [form.bankCode, form.accountNumber]);
    
    useEffect(() => {
        if (bankAccountInputValid && accountValidation?.responseMsg?.toLowerCase() === "successful" && accountValidation){
            const accountName = accountValidation?.accountName;
            const accountNumber = accountValidation?.accountNumber;

            // THESE ARE STATIC DATA TO TEST MULTIPLE TRANSFER. TO BE CHANGED LATER
            // const tempAccountName = "Abeeb Ayinla";
            setForm({...form, accountName, accountNumber});
            setValidatedBeneficiary({accountName, accountNumber});
        }
        // eslint-disable-next-line
    }, [accountValidation])
    
    useEffect(() => {
        if(accountValidationError !== null) {
            setValidationError("Error Validating Account Details")
        }
        // eslint-disable-next-line
    }, [accountValidationError])
    
    useEffect(() => {
        dispatch(banksActions.getAllBanks());
        setValidatedBeneficiary(null);
        setValidationError(null);
        dispatch(getTransferBeneficiaries({type: "inter"}))
        // eslint-disable-next-line
    }, [])
    
    useEffect(() => {
        resetValues();
        setBankPreviewItems([]);
        // eslint-disable-next-line
    }, [])
        
    return (
        <div className='w-full relative'>
            <form onSubmit={onSubmit}>
                <SelectInput
                    disabled={sourceWalletId !== null}
                    label="Source Account"
                    value={form?.walletId || ""}
                    onChange={(e) => {
                        const { value } = e.target;
                        const selectedSource = expenseAccounts?.find(account => account?.walletId === +value);
                        selectedSource && setSelectedSourceAccount(selectedSource);
                        setForm({...form, walletId: value});
                    }}
                >
                    <option value="">{businessAccountsLoading ? "Fetching source accounts..." : "Select Source Account"}</option>
                    {expenseAccounts?.map((account) => (
                        <option key={account?._id} value={account?.walletId}>{account?.accountName}</option>
                    ))}
                </SelectInput>

                <div className="form-group relative">
                    <div className="w-full flex items-center justify-between">
                        <label>Bank Name</label>
                        <div onClick={() => setBeneficiariesShown(!beneficiariesShown)} className='text-sm cursor-pointer border-b border-ep-primary-600 text-ep-primary-600 pb-[0.5px]'>
                            {beneficiariesShown ? "Close search" : "Or Search Beneficiary List"}
                        </div>
                    </div>
                    
                    {/* <select className='form-input' disabled={banksLoading} value={form.bankCode} onChange={(e) => updateBankCode(e.target.value)}>
                        <option value="">{banksLoading ? "Fetching banks..." : "Select Bank"}</option>
                        {banks?.sort((a, b) => (a?.bankName || a?.institutionName)?.localeCompare(b?.bankName || b?.institutionName))?.map(bank => (
                            <option key={bank.bankId || bank?.id} value={bank?.bankCode || bank?.institutionCode}>{bank?.bankName || bank?.institutionName}</option>
                        ))}
                    </select> */}
                    <ReactSelect
                        isDisabled={banksLoading}
                        isLoading={banksLoading}
                        isSearchable
                        name="bankCode"
                        onChange={(selected) => {
                            updateBankCode(selected.value);
                        }}
                        value={{
                            label: form.bankName || (banksLoading ? "Fetching banks..." : "Select Bank"),
                            value: form.bankCode || "",
                        }}
                        options={banksData}
                        styles={reactSelectStyles}
                        noOptionsMessage={() => "No Banks Found"}
                    />
                </div>
                    
                <div className="form-group relative">
                    <label>Account Number</label>
                    <input
                        type="text"
                        min={0}
                        id="prl-input"
                        className="form-input"
                        value={form?.accountNumber}
                        placeholder="Enter Number"
                        disabled={accountValidationLoading}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value) && value.length <= 10) {
                                setForm({...form, accountNumber: e.target.value});
                                setValidatedBeneficiary(null);
                                setValidationError(null)
                            }
                        }}
                    />
                    
                    <Maybe condition={validationError !== null}>
                        <div className="w-full flex items-center justify-start absolute -bottom-7">
                            <div className='ml-1.5 text-[#dc2626] capitalize text-xs'>
                                {validationError}
                            </div>
                        </div>
                    </Maybe>
                    
                    <Maybe condition={validatedBeneficiary !== null}>
                        <div className="w-full flex items-center justify-start absolute -bottom-7">
                            <div className="flex items-center text-sm">
                                <IconCheckCircle width={15} />
                                <div className='ml-1.5 text-[#153344] capitalize text-xs'>{validatedBeneficiary?.accountName?.toLowerCase()}</div>
                            </div>
                        </div>
                    </Maybe>
                </div>
                
                <div className="mt-14 border-t">
                    <button disabled={accountValidationLoading} type="button" onClick={addAnotherBeneficiary} className="btn btn-transparent-primary w-full disabled:cursor-not-allowed">
                        <IconAdd width={15} />&nbsp;
                        {accountValidationLoading ? "Validating..." : "Add Another"}
                    </button>
                </div>
                
                <Maybe condition={isWideViewOpen === false}>
                    <div className="w-full flex items-center justify-end pt-8">
                        <button type="button" onClick={onClose} className="btn btn-transparent-primary mr-3">
                            Cancel
                        </button>
                        
                        <button disabled type="button" className="btn btn-ep-primary">
                            Continue
                        </button>
                    </div>
                </Maybe>
            </form>
        
            <Maybe condition={beneficiariesShown}>
                <BeneficiariesList
                    isWideViewOpen={isWideViewOpen}
                    addBatchBeneficiaries={addBatchBeneficiaries}
                    loading={getTransferBeneficiariesLoading}
                    beneficiaries={getTransferBeneficiariesData?.data?.docs}
                />
            </Maybe>
        </div>
    )
}

const BeneficiariesList = ({addBatchBeneficiaries, isWideViewOpen, loading, beneficiaries}) => {
    const [selected, setSelected] = useState([]);
    const [allBeneficiaries, setAllBeneficiaries] = useState([]);
    const [batch, setBatch] = useState([]);
    const [keyword, setKeyword] = useState("");
    const { SearchBeneficiariesEmptyData, ListBeneficiariesEmptyData } = useEmptyData()
    
    const filteredBeneficiaries = allBeneficiaries?.filter((beneficiary) => {
        if(keyword === "") {
            return true
        } else {
            return beneficiary?.accountName?.toLowerCase()?.includes(keyword?.toLowerCase()) ||
                beneficiary?.bankName?.toLowerCase()?.includes(keyword?.toLowerCase()) || beneficiary?.accountNumber?.includes(keyword);
        }
    })
    
    const toggleSelection = (beneficiary) => {
        const itemInArray = batch?.indexOf(beneficiary?._id) > -1;
        if(itemInArray) {
            setBatch([...batch?.filter((item) => item !== beneficiary?._id)])
            setSelected([...selected?.filter((item) => item?._id !== beneficiary?._id)])
        } else {
            setBatch([...batch, beneficiary?._id])
            setSelected([...selected, {...beneficiary}])
        }
    }
    
    const addSelected = () => {
        const payload = selected?.map(({accountName, accountNumber, bankCode, bankName}) => ({
            accountName,
            accountNumber,
            bankCode,
            bankName
        }))
        addBatchBeneficiaries(payload)
    }
    
    useEffect(() => {
        if(beneficiaries?.length > 0) {
            setAllBeneficiaries(beneficiaries)
        }
    }, [beneficiaries])
    
    return (
        <div style={{ position: "absolute", right: isWideViewOpen ? "-310px" : "-340px", height: "420px", top: "-140px", zIndex: "50" }} className="overflow-y-scroll shadow-xl text-sm modal-list-wrap bg-white w-[300px] rounded-lg">
            <Maybe condition={loading === true}>
                <div className="py-5 text-center mt-8">
                    <div className="font-medium">
                        Fetching beneficiaries
                    </div>
                    <div className="mt-4 flex justify-center">
                        <FontAwesomeIcon icon="circle-notch" spin className="text-2xl" />
                    </div>
                </div>
            </Maybe>
            
            
            <ListBeneficiariesEmptyData condition={loading === false && allBeneficiaries?.length === 0} />
            
            <Maybe condition={loading === false && allBeneficiaries?.length > 0}>
                <div className="w-full bg-white sticky top-0 left-0 p-4">
                    <div className="w-full relative">
                        <input
                            type="text"
                            value={keyword}
                            placeholder='Search Beneficiaries' 
                            onChange={(e) => setKeyword(e.target.value)}
                            className='bg-[#f3f4f6] w-full relative text-xs pl-9 !border-0 !ring-0 rounded-lg form-input'
                        />
                        
                        <IconSearch className='absolute left-2 top-2.5' width={18} />
                    </div>
                </div>
                
                <SearchBeneficiariesEmptyData condition={keyword?.length > 0 && filteredBeneficiaries?.length === 0} />
                
                <div className="w-full p-4 pt-1">
                    <Maybe condition={selected?.length > 0}>
                        <div className="py-4 w-full flex items-center justify-start flex-wrap">
                            {selected?.map((item) => {
                                const getAccountName = (name) => {
                                    if(name?.length > 12) {
                                        return `${name?.substring(0, 12)}...`
                                    }
                                    return name
                                }
                                return (
                                    <div key={item?._id}
                                        className="mb-2 bg-[#f3f4f6] rounded-md py-1 px-1.5 text-sm capitalize flex items-center justify-between w-[47%] mr-2 whitespace-nowrap">
                                        {getAccountName(item?.accountName?.toLowerCase())}
                                        <IconRemove onClick={() => toggleSelection(item)} cursor="pointer" className='ml-2' />
                                    </div>
                                )
                            })}
                        </div>
                    </Maybe>
                    
                    <div className='pl-4'>
                        {filteredBeneficiaries?.map((beneficiary, index) => ( 
                            <div key={index} className="mb-2.5 pb-2 border-b flex items-center">
                                
                                <CustomCheckbox
                                    widthHeight={4} 
                                    checked={batch?.includes(beneficiary?._id)}
                                    onClick={() => toggleSelection(beneficiary)}
                                />
                                
                                <div className="w-3/4 pl-2">
                                    <div className="font-medium capitalize text-[#374151]">{beneficiary?.accountName?.toLowerCase()}</div>
                                    <div className="flex items-center pt-1 text-gray-500">
                                        <div>{beneficiary?.accountNumber}</div>
                                        <span className='h-1 w-1 rounded-full bg-gray-500 mx-1' />
                                        <div>{beneficiary?.bankName?.length > 15 ? beneficiary?.bankName?.substring(0, 15) + "..." : beneficiary?.bankName}</div>
                                    </div>
                                </div>
                            </div>
                            )
                        )}
                    </div>
                </div>
                
                <div className="w-full bg-white sticky bottom-0 left-0 p-4">
                    <button disabled={!selected?.length} onClick={addSelected} className="btn border border-[#d0d5dd] w-full disabled:cursor-not-allowed">
                        Add
                    </button>
                </div>
            </Maybe>
                
            <div className='sticky bottom-0 h-2 bg-white w-full left-0' />
        </div>
    )
}

export default BankMultipeTransfer